/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { getContrastTextColor } from '@sportninja/common/utils/colorContrast'
import type { PeriodCardData } from '../../hooks/useScoreboardAnimations'
import { font } from 'src/components/css'

// --- Timings (match mobile PeriodStartCard) ---
const SCALE_IN = 400
const SCALE_HOLD = 2600
const SCALE_OUT = 400
const CONTENT_FADE_IN_DELAY = 300
const CONTENT_FADE_IN = 250
const CONTENT_HOLD = 2300
const CONTENT_FADE_OUT = 200
const DISMISS_DELAY = 3200
const DISMISS_FADE = 300

// --- Keyframes ---
// Percentages are hardcoded from timing constants above to avoid
// ts-styled-plugin errors with interpolated template literals.
// SCALE: 11.8% = SCALE_IN/3400, 88.2% = (SCALE_IN+SCALE_HOLD)/3400
// CONTENT: 9.1% = CONTENT_FADE_IN/2750, 92.7% = (CONTENT_FADE_IN+CONTENT_HOLD)/2750

const scaleXKf = keyframes`
  0% { transform: scaleX(0); }
  11.8% { transform: scaleX(1); }
  88.2% { transform: scaleX(1); }
  100% { transform: scaleX(0); }
`

const contentFadeKf = keyframes`
  0% { opacity: 0; }
  9.1% { opacity: 1; }
  92.7% { opacity: 1; }
  100% { opacity: 0; }
`

const dismissKf = keyframes`
  0%   { opacity: 1; }
  100% { opacity: 0; }
`

// --- Sizes ---
// All dimensions scale proportionally using clamp().
// At 500px container width we show ~60% of max, at 1000px+ we show full size.
// Formula: clamp(min, fluid, max) where fluid = vw-based middle ground.
const CARD_MAX_WIDTH = 1000

// --- Styles ---

const outerContainer = css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 64px;
  z-index: 999;
  pointer-events: none;
  container-type: inline-size;
`

const card = css`
  width: 80%;
  max-width: ${CARD_MAX_WIDTH}px;
  background: ${colors.WHITE};
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  animation: ${scaleXKf} ${SCALE_IN + SCALE_HOLD + SCALE_OUT}ms
      cubic-bezier(0.33, 1, 0.68, 1) both,
    ${dismissKf} ${DISMISS_FADE}ms ease-in ${DISMISS_DELAY}ms both;
`

const content = css`
  width: 100%;
  animation: ${contentFadeKf}
    ${CONTENT_FADE_IN + CONTENT_HOLD + CONTENT_FADE_OUT}ms ease
    ${CONTENT_FADE_IN_DELAY}ms both;
`

const row = css`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: clamp(36px, 5cqw, 60px);
`

const logoBox = css`
  width: clamp(36px, 5cqw, 60px);
  height: clamp(36px, 5cqw, 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const logoImg = css`
  width: clamp(23px, 3.25cqw, 39px);
  height: clamp(23px, 3.25cqw, 39px);
  object-fit: contain;
`

const teamNameBox = css`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 clamp(4px, 1cqw, 12px);
  overflow: hidden;
`

const teamNameText = css`
  ${font.title};
  font-size: clamp(16px, 2.6cqw, 32px);
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const scoreBox = css`
  width: clamp(36px, 5cqw, 60px);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const scoreOverlay = css`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
`

const scoreText = css`
  ${font.title};
  font-size: clamp(18px, 2.6cqw, 32px);
  font-weight: 900;
`

const centerBox = css`
  background: ${colors.NEUTRAL_800};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 clamp(4px, 0.8cqw, 8px);
  flex-shrink: 0;
`

const periodText = css`
  ${font.title};
  font-size: clamp(14px, 2cqw, 24px);
  font-weight: 900;
  color: ${colors.WHITE};
  letter-spacing: 1px;
  text-transform: uppercase;
  white-space: nowrap;
`

const statusText = css`
  ${font.title};
  font-size: clamp(11px, 1.5cqw, 18px);
  font-weight: 600;
  color: ${colors.NEUTRAL_50};
  letter-spacing: 1px;
  text-transform: uppercase;
`

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface PeriodCardProps {
  data: PeriodCardData
}

const PeriodCard = ({ data }: PeriodCardProps) => {
  const homeTextColor = getContrastTextColor(data.homeJerseyColor)
  const visitorTextColor = getContrastTextColor(data.visitorJerseyColor)
  const isFinal = data.eventType === 'game.finalized'
  const isEnded =
    data.eventType === 'period.ended' || data.eventType === 'shootout.ended'
  const statusLabel = isFinal ? '' : isEnded ? 'ENDED' : 'STARTING'

  return (
    <div css={outerContainer}>
      <div key={data.id} css={card}>
        <div css={content}>
          <div css={row}>
            {/* Home logo */}
            <div
              css={css`
                ${logoBox};
                background: ${data.homeJerseyColor};
              `}
            >
              <img
                src={data.homeTeamLogoUrl || TEAM_PLACEHOLDER}
                alt=''
                css={logoImg}
              />
            </div>

            {/* Home name */}
            <div
              css={css`
                ${teamNameBox};
                background: ${data.homeJerseyColor};
                justify-content: flex-end;
              `}
            >
              <span
                css={css`
                  ${teamNameText};
                  color: ${homeTextColor};
                  text-align: right;
                `}
              >
                {data.homeTeamName.toUpperCase()}
              </span>
            </div>

            {/* Home score */}
            <div
              css={css`
                ${scoreBox};
                background: ${data.homeJerseyColor};
              `}
            >
              <div css={scoreOverlay}>
                <span
                  css={css`
                    ${scoreText};
                    color: ${homeTextColor};
                  `}
                >
                  {data.homeScore}
                </span>
              </div>
            </div>

            {/* Center: Period + Status */}
            <div css={centerBox}>
              <span css={periodText}>
                {isFinal ? 'FINAL' : data.periodName.toUpperCase()}
              </span>
              {statusLabel ? <span css={statusText}>{statusLabel}</span> : null}
            </div>

            {/* Visitor score */}
            <div
              css={css`
                ${scoreBox};
                background: ${data.visitorJerseyColor};
              `}
            >
              <div css={scoreOverlay}>
                <span
                  css={css`
                    ${scoreText};
                    color: ${visitorTextColor};
                  `}
                >
                  {data.visitorScore}
                </span>
              </div>
            </div>

            {/* Visitor name */}
            <div
              css={css`
                ${teamNameBox};
                background: ${data.visitorJerseyColor};
              `}
            >
              <span
                css={css`
                  ${teamNameText};
                  color: ${visitorTextColor};
                `}
              >
                {data.visitorTeamName.toUpperCase()}
              </span>
            </div>

            {/* Visitor logo */}
            <div
              css={css`
                ${logoBox};
                background: ${data.visitorJerseyColor};
              `}
            >
              <img
                src={data.visitorTeamLogoUrl || TEAM_PLACEHOLDER}
                alt=''
                css={logoImg}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PeriodCard
